import { useMemo, useState } from "react";
import { useCombobox, useMultipleSelection } from "downshift";
import { InputBaseStyle, InputDropdownSingleRowProps, InputHelp } from ".";
import classNames from "classnames";
import { FiX } from "react-icons/fi";
import { some } from "lodash";

const getFilteredOptions = (
  options: InputDropdownSingleRowProps[],
  selectedItems: InputDropdownSingleRowProps[],
  inputValue: string
): InputDropdownSingleRowProps[] => {
  const lowerCasedInputValue = inputValue.toLowerCase();
  return options.filter((option: InputDropdownSingleRowProps) => {
    return (
      !some(selectedItems, option) &&
      (option.id.toLowerCase().includes(lowerCasedInputValue) ||
        option.label.toLowerCase().includes(lowerCasedInputValue))
    );
  });
};

export default ({
  value,
  onChange,
  options,
  help,
  label,
  containerClass,
  className,
}: any) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedItems, setSelectedItems] = useState<
    InputDropdownSingleRowProps[]
  >(value || []);
  const items = useMemo(
    () => getFilteredOptions(options, selectedItems, inputValue),
    [options, selectedItems, inputValue]
  );
  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } =
    useMultipleSelection({
      selectedItems,
      onStateChange({ selectedItems: newSelectedItems, type }) {
        switch (type) {
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            onChange(newSelectedItems as InputDropdownSingleRowProps[]);
            setSelectedItems(newSelectedItems as InputDropdownSingleRowProps[]);
            break;
          default:
            break;
        }
      },
    });

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
  } = useCombobox({
    items,
    itemToString(item) {
      return item ? item.label : "";
    },
    defaultHighlightedIndex: 0, // after selection, highlight the first item.
    selectedItem: null,
    inputValue,
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep the menu open after selection.
            highlightedIndex: 0, // with the first option highlighted.
          };
        default:
          return changes;
      }
    },
    onStateChange({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem,
    }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (newSelectedItem) {
            setSelectedItems([...selectedItems, newSelectedItem]);
            onChange([...selectedItems, newSelectedItem]);
            setInputValue("");
          }
          break;

        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue || "");
          break;
        default:
          break;
      }
    },
  });

  return (
    <div
      className={`flex flex-col justify-start items-start gap-2 w-full ${containerClass}`}
    >
      {label && (
        <label
          {...getLabelProps()}
          className="text-left text-base text-gray font-medium"
        >
          {label}{" "}
          <span className="font-light text-sm">
            ({options.length} options available)
          </span>
        </label>
      )}
      <div className="flex flex-wrap gap-2">
        {selectedItems.map(function renderSelectedItem(
          selectedItemForRender,
          index
        ) {
          return (
            <span
              className="bg-uw-secondary text-uw-text-secondary rounded-md px-2 py-1 focus:bg-red-400 flex gap-1 items-center"
              key={`selected-item-${index}`}
              {...getSelectedItemProps({
                selectedItem: selectedItemForRender,
                index,
              })}
            >
              {selectedItemForRender.label}
              <span
                className="px-1 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  removeSelectedItem(selectedItemForRender);
                }}
              >
                <FiX />
              </span>
            </span>
          );
        })}
      </div>
      <div className="relative w-full">
        <InputBaseStyle
          className={className}
          {...getInputProps(getDropdownProps({ preventKeyAction: isOpen }))}
        />
        <ul
          {...getMenuProps()}
          className="absolute z-10 bg-white max-h-[50vh] overflow-auto"
        >
          {isOpen &&
            items.map((item, index) => (
              <li
                className={classNames(
                  highlightedIndex === index && "bg-blue-300",
                  selectedItem === item && "font-bold",
                  "py-2 px-3 shadow-sm flex flex-col"
                )}
                key={item.id}
                {...getItemProps({ item, index })}
              >
                <span>{item.label}</span>
              </li>
            ))}
        </ul>
      </div>
      {help && <InputHelp>{help}</InputHelp>}
    </div>
  );
};
